// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Card symbol generation utility
 * Generates deterministic, unique symbol combinations for cards
 */

// Curated pool of FontAwesome solid icon names (without 'fa-' prefix)
// Categories: Medical, Objects, Nature, Abstract, Actions
export const SYMBOL_POOL = [
  // Medical/Psychology
  'brain',
  'syringe',
  'pills',
  'stethoscope',
  'heart-pulse',
  'dna',
  'microscope',
  'vial',
  'bandage',
  'hospital',
  // Objects
  'flask',
  'couch',
  'book',
  'clock',
  'key',
  'lightbulb',
  'puzzle-piece',
  'bell',
  'gem',
  'crown',
  // Nature
  'moon',
  'sun',
  'cloud',
  'tree',
  'star',
  'leaf',
  'snowflake',
  'fire',
  'water',
  'mountain',
  // Abstract/Concepts
  'bolt',
  'ghost',
  'eye',
  'hand',
  'shield',
  'infinity',
  'yin-yang',
  'atom',
  'circle-notch',
  'rotate',
  // Animals/Creatures
  'dove',
  'dragon',
  'spider',
  'fish',
  'cat',
  'dog',
  'crow',
  'frog',
  'horse',
  'bug',
  // More objects
  'anchor',
  'binoculars',
  'bomb',
  'camera',
  'compass',
  'feather',
  'hammer',
  'magnet',
  'paperclip',
  'rocket',
  'scroll',
  'trophy',
  'umbrella',
  'wand-magic-sparkles',
  'wine-glass',
] as const;

export type SymbolName = (typeof SYMBOL_POOL)[number];

/**
 * Simple hash function for strings
 * Produces consistent numeric hash from any string
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate three deterministic symbols for a card based on its ID
 * Same card ID will always produce the same symbol combination
 */
export function getCardSymbols(cardId: string): [SymbolName, SymbolName, SymbolName] {
  const hash = hashString(cardId);
  const poolSize = SYMBOL_POOL.length;

  // Use different bit ranges to ensure varied selection
  const index1 = hash % poolSize;
  const index2 = Math.floor(hash / poolSize) % poolSize;
  const index3 = Math.floor(hash / (poolSize * poolSize)) % poolSize;

  // Ensure all three are different
  let finalIndex2 = index2;
  let finalIndex3 = index3;

  if (finalIndex2 === index1) {
    finalIndex2 = (finalIndex2 + 1) % poolSize;
  }

  if (finalIndex3 === index1 || finalIndex3 === finalIndex2) {
    finalIndex3 = (finalIndex3 + 1) % poolSize;
  }
  if (finalIndex3 === index1 || finalIndex3 === finalIndex2) {
    finalIndex3 = (finalIndex3 + 1) % poolSize;
  }

  return [SYMBOL_POOL[index1], SYMBOL_POOL[finalIndex2], SYMBOL_POOL[finalIndex3]];
}

/**
 * Convert symbol name to FontAwesome icon reference
 * For use with FontAwesomeIcon component
 */
export function symbolToIcon(symbolName: SymbolName): string {
  return symbolName;
}

/**
 * Check if two symbol combinations are the same
 */
export function symbolsMatch(
  a: [SymbolName, SymbolName, SymbolName],
  b: [SymbolName, SymbolName, SymbolName]
): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
